import { Card, Spin } from 'antd'
import type { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'
import React, { useMemo } from 'react'
import { formatCompact, formatLocale } from 'src/util/Util'

interface HorizontalBarChartProps {
    title: string
    labels: string[]
    data: number[]
    max: number
    loading: boolean
    color: string
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ title, labels, data, max, loading, color }) => {

    const maxLabelWidth = useMemo(() => {

        if (!labels || labels.length === 0) return 100

        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        if (!context) return 100

        let maxWidth = 0
        labels.forEach((label) => {
            const metrics = context.measureText(label)
            if (metrics.width > maxWidth) {
                maxWidth = metrics.width
            }
        })

        return Math.ceil(maxWidth + 40)
    }, [labels])

    const option: EChartsOption = {
        grid: { left: maxLabelWidth, right: 20, top: 20, bottom: 20 },
        xAxis: {
            type: 'value' as const,
            position: 'top' as const,
            axisLine: { show: false },
            axisTick: { show: false },
            min: 0,
            max: max ?? 100,
            splitLine: {
                show: true,
                lineStyle: {
                    color: '#e0e0e0',
                    type: 'dashed' // dashed vertical grid lines
                }
            },
            axisLabel: {
                formatter: (value) => formatCompact(value, v => '$' + v),
                hideOverlap: true
            }
        },
        yAxis: {
            type: 'category' as const,
            data: labels,
            inverse: true,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: {
                show: true,
                lineStyle: {
                    color: '#e0e0e0',
                    type: 'dashed' // dashed horizontal grid lines
                }
            }
        },
        tooltip: {
            trigger: 'axis' as const,
            axisPointer: { type: 'shadow' as const },
            formatter: (params) => params.map(param => {
                const value = formatLocale(param.value, v => '$' + v)
                return `<b>${param.name}</b><br/>${value}`
            }).join('')
        },
        series: [
            {
                type: 'bar' as const,
                data,
                itemStyle: { color },
                barWidth: '60%',
                label: {
                    show: true,             // display the label
                    position: 'insideLeft',
                    formatter: (params) => {
                        return formatCompact(params.value, v => '$' + v)
                    }
                }
            }
        ]
    }

    return <Card
        title={title}
        style={{ borderRadius: 8 }}
        bodyStyle={{ padding: 12 }}
        headStyle={{ fontWeight: 'bold' }}
    >
        {loading ?
            <Spin size="large" style={{ display: 'block', margin: '20px auto' }} /> : !data || data.length === 0 ? (
                <div style={{
                    height: 70,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#999'
                }}>No data available</div>
            ) : (
                <ReactECharts
                    option={option}
                    style={{
                        height: `${Math.max(labels.length * 35 + 35, 70)}px`,
                        width: '100%'
                    }}
                />
            )}
    </Card>
}

export default HorizontalBarChart


// WEBPACK FOOTER //
// ./src/component/app/dashboard/HorizontalBarChart.tsx